import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { COMPANY_INFO } from '../constants';
import { API_EMAIL_URL } from '../api-config';
import { MapPin, Phone, Mail, Clock, Send, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';

const Contact: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.message) {
      setErrorMessage('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch(API_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(data.message);
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  return (
    <div className="pt-40 pb-20 bg-slate-50 min-h-screen px-6 lg:px-12 text-slate-900">
      <div className="max-w-7xl mx-auto">
        <motion.div 
           initial={{ opacity: 0, y: 30 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.8 }}
           className="mb-20 text-center"
        >
           <span className="text-gold-600 font-bold uppercase tracking-widest text-xs block mb-4">Formulaire de contact</span>
           <h1 className="font-serif text-5xl md:text-7xl mb-6 text-black">Contactez-nous</h1>
           <p className="text-slate-500 text-xl font-light max-w-2xl mx-auto">
              Une question spécifique ? <br />
              Notre équipe est à votre écoute 24h/24 et 7j/7.
           </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
           
           {/* Left: Contact Info */}
           <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="space-y-12"
           >
              <div className="p-8 bg-white border border-slate-200 rounded-sm shadow-xl shadow-slate-200/50">
                 <h3 className="font-serif text-2xl mb-8 text-black">Nos Coordonnées</h3>
                 
                 <div className="space-y-8">
                    <div className="flex items-start gap-6">
                       <div className="w-12 h-12 bg-black text-white flex items-center justify-center rounded-full shrink-0">
                          <MapPin size={24} />
                       </div>
                       <div>
                          <span className="block text-slate-400 text-xs uppercase tracking-widest mb-1">Adresse</span>
                          <p className="text-lg font-light leading-relaxed text-slate-800">{COMPANY_INFO.address}</p>
                       </div>
                    </div>

                    <div className="flex items-start gap-6">
                       <div className="w-12 h-12 bg-black text-white flex items-center justify-center rounded-full shrink-0">
                          <Phone size={24} />
                       </div>
                       <div>
                          <span className="block text-slate-400 text-xs uppercase tracking-widest mb-1">Téléphone</span>
                          <a href={`tel:${COMPANY_INFO.phone}`} className="text-lg font-light hover:text-gold-600 transition-colors text-slate-800">{COMPANY_INFO.phone}</a>
                          
                       </div>
                    </div>

                    <div className="flex items-start gap-6">
                       <div className="w-12 h-12 bg-black text-white flex items-center justify-center rounded-full shrink-0">
                          <Mail size={24} />
                       </div>
                       <div>
                          <span className="block text-slate-400 text-xs uppercase tracking-widest mb-1">Email</span>
                          <a href={`mailto:${COMPANY_INFO.email}`} className="text-lg font-light hover:text-gold-600 transition-colors text-slate-800">{COMPANY_INFO.email}</a>
                         
                       </div>
                    </div>
                 </div>
              </div>
              
              {/* Map Placeholder */}
              <div className="h-64 bg-white border border-slate-200 relative overflow-hidden group shadow-lg">
                 <img src="/map-suisse.webp" alt="Lausanne Map Background" className="w-full h-full object-cover opacity-60 grayscale group-hover:grayscale-0 transition-all duration-700" />
                 <div className="absolute inset-0 flex items-center justify-center">
                    <a href="https://maps.app.goo.gl/LeXRhxFU16QZ2B849" target="_blank" rel="noopener noreferrer" className="bg-white/90 backdrop-blur-md px-6 py-3 border border-slate-200 text-black hover:bg-black hover:text-white transition-all uppercase text-xs font-bold tracking-widest flex items-center gap-2 shadow-lg">
                       Voir sur la carte <ArrowRight size={14} />
                    </a>
                 </div>
              </div>
           </motion.div>

           {/* Right: Form or Success Page */}
           <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
           >
              {status === 'success' ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-8 bg-white p-8 md:p-12 border border-slate-200 shadow-xl shadow-slate-200/50 text-center"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="font-serif text-3xl mb-4 text-black">Message envoyé !</h3>
                  <p className="text-slate-600 text-lg leading-relaxed">
                    Merci <strong>{form.name}</strong> !<br />
                    Nous avons bien reçu votre message.
                  </p>
                
                  <button 
                    onClick={() => { setStatus('idle'); setForm({ name: '', email: '', subject: '', message: '' }); }}
                    className="bg-black text-white px-8 py-3 font-bold uppercase tracking-widest hover:bg-gold-600 transition-colors"
                  >
                    Envoyer un autre message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8 bg-white p-8 md:p-12 border border-slate-200 shadow-xl shadow-slate-200/50">
                  <h3 className="font-serif text-2xl mb-8 text-black">Envoyer un message</h3>
                  
                  {errorMessage && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-sm">
                      {errorMessage}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="group">
                      <label className="block text-slate-500 text-xs uppercase tracking-widest mb-2">Nom *</label>
                      <input 
                        type="text" 
                        name="name" 
                        value={form.name}
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-3 text-slate-900 focus:outline-none focus:border-gold-500 transition-colors"
                        onChange={handleChange}
                      />
                    </div>
                    <div className="group">
                      <label className="block text-slate-500 text-xs uppercase tracking-widest mb-2">Email *</label>
                      <input 
                        type="email" 
                        name="email" 
                        value={form.email}
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-3 text-slate-900 focus:outline-none focus:border-gold-500 transition-colors"
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="group">
                    <label className="block text-slate-500 text-xs uppercase tracking-widest mb-2">Sujet</label>
                    <input 
                      type="text" 
                      name="subject" 
                      value={form.subject}
                      className="w-full bg-slate-50 border-b-2 border-slate-200 py-3 text-slate-900 focus:outline-none focus:border-gold-500 transition-colors"
                      onChange={handleChange}
                    />
                  </div>

                  <div className="group">
                    <label className="block text-slate-500 text-xs uppercase tracking-widest mb-2">Message *</label>
                    <textarea 
                      name="message" 
                      rows={4} 
                      value={form.message}
                      className="w-full bg-slate-50 border-b-2 border-slate-200 py-3 text-slate-900 focus:outline-none focus:border-gold-500 transition-colors resize-none"
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    disabled={status === 'loading'}
                    className="bg-black text-white px-10 py-4 font-bold uppercase tracking-widest hover:bg-gold-600 transition-colors w-full flex justify-center items-center gap-3 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 className="animate-spin" size={18} /> Envoi en cours...
                      </>
                    ) : (
                      <>Envoyer <Send size={18} /></>
                    )}
                  </button>
                </form>
              )}
           </motion.div>

        </div>
      </div>
    </div>
  );
};

export default Contact;